import {
  IfpCompanyForm,
  IfpEmploymentType,
  IfpOccupation,
  IfpWorkingHours,
  TimeUntilEmployedAgainEstimation,
} from '@shared/sec-loans'
import { BusinessIdInput } from '@ui-common/components/BusinessIdInput'
import { DateInputField } from '@ui-common/components/DateInputField'
import { YesNo } from '@ui-common/types'
import { buildOptionList } from '@ui-common/utils'
import { FormRadioGroup } from '@ui-components/form/FormRadioGroup'
import { FormTextInput } from '@ui-components/form/FormTextInput'
import { ReactNode } from 'react'
import { useFormContext, useFormState, useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { getIfpApplicantInfoLocalizedEnums } from './applicantInfoStepUtils'
import { ApplicantInfoFormData } from './types'

export const OccupationFormPart = () => {
  const { formatMessage } = useIntl()
  const { register, control } = useFormContext<ApplicantInfoFormData>()
  const { errors } = useFormState({ name: ['occupation'], control })

  const { occupationLabels } = getIfpApplicantInfoLocalizedEnums(formatMessage)
  const [selectedOccupation] = useWatch({ control, name: ['occupation'] })

  const occupationFormFieldsMap: Record<IfpOccupation, () => ReactNode> = {
    [IfpOccupation.Pensioner]: () => <PensionerFormFields />,
    [IfpOccupation.Employed]: () => <EmployedFormFields />,
    [IfpOccupation.Furlough]: () => <FurloughFormFields />,
    [IfpOccupation.Entrepreneur]: () => <EntrepreneurFormFields />,
    [IfpOccupation.LongtermSickLeave]: () => <AbsenceOfLeaveOccupationFormFields />,
    [IfpOccupation.Unemployed]: () => <UnemployedFormFields />,
    [IfpOccupation.OnFamilyLeave]: () => <AbsenceOfLeaveOccupationFormFields />,
    [IfpOccupation.StudyLeave]: () => <AbsenceOfLeaveOccupationFormFields />,
    [IfpOccupation.Student]: () => null,
    [IfpOccupation.Other]: () => null,
  }

  const renderOccupationFormFields = selectedOccupation && occupationFormFieldsMap[selectedOccupation]

  return (
    <FormRadioGroup
      label={formatMessage({ id: 'ifp-loans-applicant-info-everyday-life-label' })}
      description={formatMessage({ id: 'ifp-loans-applicant-info-everyday-life-description' })}
      required={true}
      options={buildOptionList(Object.values(IfpOccupation), occupationLabels)}
      error={errors.occupation?.message}
      {...register('occupation')}
    >
      {renderOccupationFormFields?.()}
    </FormRadioGroup>
  )
}

const CommonEmploymentInfoFields = () => {
  const { formatMessage } = useIntl()
  const { register, control, getValues } = useFormContext<ApplicantInfoFormData>()
  const { errors } = useFormState({
    name: ['employmentType', 'employer', 'workingHours', 'profession', 'employmentStartDate', 'employmentEndDate'],
    control,
  })
  const [selectedEmploymentTypeWatch] = useWatch({
    control,
    name: ['employmentType'],
  })
  const selectedEmploymentType = selectedEmploymentTypeWatch ?? getValues().employmentType

  const { employmentTypeLabels, workingHoursLabels } = getIfpApplicantInfoLocalizedEnums(formatMessage)

  return (
    <>
      <FormRadioGroup
        required={true}
        label={formatMessage({ id: 'employment-info-employment-type-label' })}
        options={buildOptionList(Object.values(IfpEmploymentType), employmentTypeLabels)}
        error={errors?.employmentType?.message}
        {...register('employmentType')}
      />
      <FormTextInput
        required={true}
        label={formatMessage({ id: 'employment-info-employer-label' })}
        error={errors?.employer?.message}
        {...register('employer')}
      />
      <FormRadioGroup
        required={true}
        label={formatMessage({ id: 'sec-loans-employment-info-working-hours-form-label' })}
        options={buildOptionList(Object.values(IfpWorkingHours), workingHoursLabels)}
        error={errors?.workingHours?.message}
        {...register('workingHours')}
      />
      <FormTextInput
        required={true}
        label={formatMessage({ id: 'common-employment-info-profession-label' })}
        error={errors?.profession?.message}
        {...register('profession')}
      />
      <DateInputField
        required={true}
        label={formatMessage({ id: 'ifp-loans-applicant-info-employment-start-date-label' })}
        error={errors.employmentStartDate?.message}
        fieldName="employmentStartDate"
      />
      {selectedEmploymentType === IfpEmploymentType.TimeLimited && (
        <DateInputField
          required={true}
          label={formatMessage({ id: 'ifp-loans-applicant-info-employment-end-date-label' })}
          error={errors.employmentEndDate?.message}
          fieldName="employmentEndDate"
        />
      )}
    </>
  )
}

const PensionerFormFields = () => {
  const { formatMessage } = useIntl()
  const { register, control } = useFormContext<ApplicantInfoFormData>()
  const { errors } = useFormState({ name: ['grossSalary', 'netSalary'], control })

  return (
    <>
      <FormTextInput
        required={true}
        inputMode="decimal"
        label={formatMessage({ id: 'sec-loans-income-info-gross-salary-for-pensioners-form-label' })}
        error={errors?.grossSalary?.message}
        {...register('grossSalary')}
      />
      <FormTextInput
        required={true}
        inputMode="decimal"
        label={formatMessage({ id: 'sec-loans-income-info-net-salary-for-pensioners-form-label' })}
        error={errors?.netSalary?.message}
        {...register('netSalary')}
      />
    </>
  )
}

const EmployedFormFields = () => {
  const { formatMessage } = useIntl()
  const { register, control } = useFormContext<ApplicantInfoFormData>()
  const { errors } = useFormState({
    name: ['grossSalary', 'netSalary'],
    control,
  })

  return (
    <>
      <CommonEmploymentInfoFields />
      <FormTextInput
        required={true}
        inputMode="decimal"
        label={formatMessage({ id: 'sec-loans-income-info-gross-salary-form-label' })}
        error={errors?.grossSalary?.message}
        {...register('grossSalary')}
      />
      <FormTextInput
        required={true}
        inputMode="decimal"
        label={formatMessage({ id: 'sec-loans-income-info-net-salary-form-label' })}
        error={errors?.netSalary?.message}
        {...register('netSalary')}
      />
    </>
  )
}

const FurloughFormFields = () => {
  const { formatMessage } = useIntl()
  const { register, control } = useFormContext<ApplicantInfoFormData>()
  const { errors } = useFormState({
    name: ['normalGrossSalary', 'normalNetSalary', 'estimatedFurloughEndDate'],
    control,
  })

  return (
    <>
      <DateInputField
        required={true}
        label={formatMessage({ id: 'ifp-loans-applicant-info-estimated-furlough-end-date-label' })}
        fieldName="estimatedFurloughEndDate"
        error={errors?.estimatedFurloughEndDate?.message}
      />
      <CurrentIncomeFormFields />
      <FormTextInput
        required={true}
        inputMode="decimal"
        label={formatMessage({ id: 'ifp-loans-applicant-info-normal-gross-salary-label' })}
        error={errors?.normalGrossSalary?.message}
        {...register('normalGrossSalary')}
      />
      <FormTextInput
        required={true}
        inputMode="decimal"
        label={formatMessage({ id: 'ifp-loans-applicant-info-normal-net-salary-label' })}
        error={errors?.normalNetSalary?.message}
        {...register('normalNetSalary')}
      />
      <CommonEmploymentInfoFields />
    </>
  )
}

const EntrepreneurFormFields = () => {
  const { formatMessage } = useIntl()
  const { register, control } = useFormContext<ApplicantInfoFormData>()
  const { errors } = useFormState({
    name: ['companyName', 'incomeBeforeTaxes', 'incomeAfterTaxes', 'legalFormOfTheCompany', 'companyFoundingDate'],
    control,
  })

  const { companyLegalFormLabels } = getIfpApplicantInfoLocalizedEnums(formatMessage)

  return (
    <>
      <FormTextInput
        required={true}
        label={formatMessage({ id: 'ifp-loans-applicant-info-company-name-label' })}
        error={errors?.companyName?.message}
        {...register('companyName')}
      />
      <BusinessIdInput required={true} name="businessId" />
      <FormTextInput
        required={true}
        inputMode="decimal"
        label={formatMessage({ id: 'ifp-loans-applicant-info-income-before-taxes-label' })}
        error={errors?.incomeBeforeTaxes?.message}
        {...register('incomeBeforeTaxes')}
      />
      <FormTextInput
        required={true}
        inputMode="decimal"
        label={formatMessage({ id: 'ifp-loans-applicant-info-income-after-taxes-label' })}
        error={errors?.incomeAfterTaxes?.message}
        {...register('incomeAfterTaxes')}
      />
      <FormRadioGroup
        required={true}
        label={formatMessage({ id: 'sec-loans-entrepreneur-info-company-legal-form' })}
        options={buildOptionList(Object.values(IfpCompanyForm), companyLegalFormLabels)}
        error={errors?.legalFormOfTheCompany?.message}
        {...register('legalFormOfTheCompany')}
      />
      <DateInputField
        required={true}
        label={formatMessage({ id: 'ifp-loans-applicant-info-company-foundation-date-label' })}
        fieldName="companyFoundingDate"
        error={errors?.companyFoundingDate?.message}
      />
    </>
  )
}

const UnemployedFormFields = () => {
  return (
    <>
      <CurrentIncomeFormFields />
      <ReEmploymentFormFields />
    </>
  )
}

const AbsenceOfLeaveOccupationFormFields = () => {
  const { formatMessage } = useIntl()
  const { register, control, getValues } = useFormContext<ApplicantInfoFormData>()
  const { errors } = useFormState({
    name: ['hasJobToReturnTo', 'estimatedReturnToWorkDate', 'normalGrossSalary', 'normalNetSalary'],
    control,
  })

  const [hasJobToReturnToWatch] = useWatch({
    name: ['hasJobToReturnTo'],
    control,
  })
  const hasJobToReturnTo = hasJobToReturnToWatch ?? getValues().hasJobToReturnTo

  const { yesNoTexts } = getIfpApplicantInfoLocalizedEnums(formatMessage)

  return (
    <>
      <CurrentIncomeFormFields />
      <FormRadioGroup
        required={true}
        label={formatMessage({ id: 'ifp-loans-applicant-info-has-job-to-return-to-label' })}
        options={buildOptionList(Object.values(YesNo), yesNoTexts)}
        error={errors?.hasJobToReturnTo?.message}
        {...register('hasJobToReturnTo')}
      >
        {hasJobToReturnTo === YesNo.Yes && (
          <>
            <DateInputField
              required={true}
              label={formatMessage({ id: 'ifp-loans-applicant-info-estimated-return-to-work-date' })}
              fieldName="estimatedReturnToWorkDate"
              error={errors?.estimatedReturnToWorkDate?.message}
            />
            <FormTextInput
              required={true}
              inputMode="decimal"
              label={formatMessage({ id: 'ifp-loans-applicant-info-normal-gross-salary-label' })}
              error={errors?.normalGrossSalary?.message}
              {...register('normalGrossSalary')}
            />
            <FormTextInput
              required={true}
              inputMode="decimal"
              label={formatMessage({ id: 'ifp-loans-applicant-info-normal-net-salary-label' })}
              error={errors?.normalNetSalary?.message}
              {...register('normalNetSalary')}
            />
            <CommonEmploymentInfoFields />
          </>
        )}
        {hasJobToReturnTo === YesNo.No && <ReEmploymentFormFields />}
      </FormRadioGroup>
    </>
  )
}

const CurrentIncomeFormFields = () => {
  const { formatMessage } = useIntl()
  const { register, control } = useFormContext<ApplicantInfoFormData>()
  const { errors } = useFormState({
    name: ['currentIncomeBeforeTaxes', 'currentIncomeAfterTaxes'],
    control,
  })

  return (
    <>
      <FormTextInput
        required={true}
        inputMode="decimal"
        label={formatMessage({ id: 'ifp-loans-applicant-info-current-income-before-taxes-label' })}
        error={errors?.currentIncomeBeforeTaxes?.message}
        {...register('currentIncomeBeforeTaxes')}
      />
      <FormTextInput
        required={true}
        inputMode="decimal"
        label={formatMessage({ id: 'ifp-loans-applicant-info-current-income-after-taxes-label' })}
        error={errors?.currentIncomeAfterTaxes?.message}
        {...register('currentIncomeAfterTaxes')}
      />
    </>
  )
}

const ReEmploymentFormFields = () => {
  const { formatMessage } = useIntl()
  const { register, control, getValues } = useFormContext<ApplicantInfoFormData>()
  const { errors } = useFormState({
    name: ['expectsToBeEmployedAgain', 'timeUntilEmployedAgainEstimation'],
    control,
  })

  const { timeUntilEmployedAgainEstimationLabels, yesNoTexts } = getIfpApplicantInfoLocalizedEnums(formatMessage)

  const [expectsToBeEmployedAgainWatch] = useWatch({
    name: ['expectsToBeEmployedAgain'],
    control,
  })
  const expectsToBeEmployedAgain = expectsToBeEmployedAgainWatch ?? getValues().expectsToBeEmployedAgain

  return (
    <FormRadioGroup
      required={true}
      label={formatMessage({ id: 'ifp-loans-applicant-info-expects-to-be-employed-again-label' })}
      options={buildOptionList(Object.values(YesNo), yesNoTexts)}
      error={errors?.expectsToBeEmployedAgain?.message}
      {...register('expectsToBeEmployedAgain')}
    >
      {expectsToBeEmployedAgain === YesNo.Yes && (
        <FormRadioGroup
          required={true}
          label={formatMessage({ id: 'ifp-loans-applicant-info-time-until-employed-again-estimation-label' })}
          options={buildOptionList(
            Object.values(TimeUntilEmployedAgainEstimation),
            timeUntilEmployedAgainEstimationLabels,
          )}
          error={errors?.timeUntilEmployedAgainEstimation?.message}
          {...register('timeUntilEmployedAgainEstimation')}
        />
      )}
    </FormRadioGroup>
  )
}
